import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, AlertTriangle, Loader2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { roleLabel } from "@/lib/constants";

/**
 * /invite/:token — public page where a newly-invited team member sets their
 * own password. Validates the token server-side; on success, activates the
 * account and immediately signs the user in.
 */
export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/invites/${token}`);
        setInvite(data);
      } catch (err) {
        setError(err.response?.data?.detail || "This invite is no longer valid.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/invites/accept`, { token, new_password: pw });
      localStorage.setItem("mfd_token", data.token);
      localStorage.setItem("mfd_user", JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.name.split(" ")[0]} — you're signed in.`);
      setLoading && setLoading(false);
      // Full reload so AuthProvider picks up the new user from storage
      window.location.href = "/app";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="marketing hero-bg min-h-screen">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-6 py-4">
          <BrandLogo size="md" />
        </div>
      </header>
      <div className="mx-auto max-w-md px-6 py-16">
        <div className="mb-6 flex items-center gap-3 text-sm text-primary">
          <ShieldCheck className="h-4 w-4" /> Welcome — one quick step to activate your account.
        </div>

        {error && (
          <Card className="border-primary/40 bg-primary/5 p-6" data-testid="invite-error-card">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <h2 className="font-dashboard text-lg font-bold">Invite unavailable</h2>
                <p className="mt-1 text-sm text-muted-foreground">{error}</p>
                <p className="mt-3 text-xs text-muted-foreground">Ask your Admin to send you a fresh invite from the Team Members page.</p>
              </div>
            </div>
          </Card>
        )}

        {!error && invite && (
          <Card className="border-border bg-white p-8" data-testid="invite-form-card">
            <div className="mb-2 overline text-muted-foreground">Setting up:</div>
            <h1 className="font-dashboard text-2xl font-bold tracking-tight">{invite.name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{invite.email} · {roleLabel(invite.role)}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">Choose your password</Label>
                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="invite-pw-input" />
                <p className="mt-1 text-[11px] text-muted-foreground">At least 8 characters.</p>
              </div>
              <div>
                <Label htmlFor="conf">Confirm password</Label>
                <Input id="conf" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="invite-confirm-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="invite-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                {busy ? "Activating…" : "Activate my account"}
              </Button>
            </form>
          </Card>
        )}

        {!error && !invite && (
          <Card className="border-border bg-white p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-primary" />
            Verifying your invite…
          </Card>
        )}
      </div>
    </div>
  );
}
